/* eslint-disable react-refresh/only-export-components */
import {
  DimeButton,
  DimeCard,
  DimeCardAction,
  DimeCardContent,
  DimeCardDescription,
  DimeCardFooter,
  DimeCardHeader,
  DimeCardTitle,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';

import { CardDocsPreview } from './-CardDocsPreview.js';
import { CardDocsSection } from './-CardDocsSection.js';
import { CardMetricShellDocs } from './-CardMetricShellDocs.js';
import { CardRecordShellDocs } from './-CardRecordShellDocs.js';
import { CardSectionShellDocs } from './-CardSectionShellDocs.js';

export const Route = createFileRoute('/design-system/card')({
  component: CardDocs,
});

function CardDemo() {
  return (
    <DimeCard className="w-full max-w-sm">
      <DimeCardHeader>
        <DimeCardTitle>Notifications</DimeCardTitle>
        <DimeCardDescription>You have 3 unread messages.</DimeCardDescription>
        <DimeCardAction>
          <DimeButton variant="outline" size="sm">
            Mark all read
          </DimeButton>
        </DimeCardAction>
      </DimeCardHeader>
      <DimeCardContent className="flex flex-col gap-3">
        {[
          { title: 'Your call has been confirmed.', time: '1 hour ago' },
          { title: 'You have a new message!', time: '1 hour ago' },
          { title: 'Your subscription is expiring soon!', time: '2 hours ago' },
        ].map(({ title, time }) => (
          <div key={title} className="flex items-start gap-3">
            <span className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" />
            <div>
              <p className="text-sm font-medium">{title}</p>
              <p className="text-xs text-muted-foreground">{time}</p>
            </div>
          </div>
        ))}
      </DimeCardContent>
      <DimeCardFooter>
        <DimeButton className="w-full">View all notifications</DimeButton>
      </DimeCardFooter>
    </DimeCard>
  );
}

function CardSmall() {
  return (
    <DimeCard size="sm" className="w-full max-w-sm">
      <DimeCardHeader>
        <DimeCardTitle>Team members</DimeCardTitle>
        <DimeCardDescription>Invite colleagues to collaborate.</DimeCardDescription>
      </DimeCardHeader>
      <DimeCardContent className="flex flex-col gap-2">
        {['Test User', 'Jane Smith', 'Alex Johnson'].map((name) => (
          <div key={name} className="flex items-center justify-between">
            <p className="text-sm">{name}</p>
            <DimeButton variant="ghost" size="sm">
              Remove
            </DimeButton>
          </div>
        ))}
      </DimeCardContent>
      <DimeCardFooter>
        <DimeButton variant="outline" size="sm" className="w-full">
          Invite member
        </DimeButton>
      </DimeCardFooter>
    </DimeCard>
  );
}

function CardImage() {
  return (
    <DimeCard className="w-full max-w-sm overflow-hidden">
      <div className="h-40 w-full bg-gradient-to-br from-primary/20 to-primary/5" />
      <DimeCardHeader>
        <DimeCardTitle>Getting started</DimeCardTitle>
        <DimeCardDescription>
          Learn how to set up your workspace and invite your team.
        </DimeCardDescription>
      </DimeCardHeader>
      <DimeCardContent>
        <p className="text-sm text-muted-foreground">
          Follow the quick-start guide to configure integrations and customize your dashboard.
        </p>
      </DimeCardContent>
      <DimeCardFooter>
        <DimeButton size="sm">Read guide</DimeButton>
        <DimeButton variant="ghost" size="sm">
          Dismiss
        </DimeButton>
      </DimeCardFooter>
    </DimeCard>
  );
}

function CardDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Card</h1>
      <p className="mb-8 text-muted-foreground">
        <code>DimeCard</code> is the primitive. For a record, pick a shell instead of assembling
        header, title, and content by hand: <code>DimeCardShell</code> for the identity header,{' '}
        <code>DimeDetailCardShell</code> for a titled section of facts or a child list,{' '}
        <code>DimeMetricCardShell</code> for a KPI. The primitive demos at the bottom are the escape
        hatch — a notification stack, an image tile.
      </p>

      <CardRecordShellDocs />
      <CardSectionShellDocs />
      <CardMetricShellDocs />

      <CardDocsSection title="DimeCard">
        <p className="mb-4 text-sm text-muted-foreground">
          Use the primitive when no shell fits. Pair with <code>DimeCardHeader</code>/
          <code>Title</code>/<code>Description</code>/<code>Content</code>/<code>Footer</code>/
          <code>Action</code>.
        </p>
        <CardDocsPreview className="flex items-start justify-center">
          <CardDemo />
        </CardDocsPreview>
      </CardDocsSection>

      <CardDocsSection title="Size">
        <CardDocsPreview className="flex items-start justify-center">
          <CardSmall />
        </CardDocsPreview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">size="sm"</code> on{' '}
          <code className="font-mono">DimeCard</code> for tighter spacing.
        </p>
      </CardDocsSection>

      <CardDocsSection title="Image">
        <CardDocsPreview className="flex items-start justify-center">
          <CardImage />
        </CardDocsPreview>
        <p className="mt-3 text-sm text-muted-foreground">
          Place any content (image, banner, gradient) before{' '}
          <code className="font-mono">DimeCardHeader</code> to create a card with a visual header.
        </p>
      </CardDocsSection>
    </div>
  );
}
